import {createSelector} from 'reselect'
import isEqual from 'lodash/isEqual.js'
import mapValues from 'lodash/mapValues.js'

import {
  PURCHASE_ORDER_PLURAL_URI_COMPONENT,
  PURCHASE_ORDER_SINGLE_URI_COMPONENT,
  PURCHASE_ORDER_STATII,
  DEFAULT_STATUS,
  DEFAULT_SORT,
  DEFAULT_PER_PAGE,
  DEFAULT_EXPAND_MODE,
  PURCHASE_ORDER_STATUS_LIST,
} from '../../common/constants/PurchaseOrders.js'
import {buildPath, stringifyURL} from '../../common/querystring.js'
import {isPresent} from '../../common/utils.js'
import {
  EXPAND_MODE_EXPANDED,
  EXPAND_MODE_COLLAPSED,
} from '../../common/components/List/ExpandAllButton.js'
import {formsSelector} from '../../store.js'
import {locationSelector} from '../../redux/selectors/ui/location.js'
import {poTagsSortedByNameSelector} from '../../data/poTags.js'
import {posSelector} from '../../data/pos.js'
import {
  ensureArray,
  ensureArrayOfNumbers,
  ensureArrayOrUndefined,
  ensureBoolean,
  ensureTroolean,
} from '../../common/ensure.js'
import {TAG_FILTER_OR} from '../../common/constants/Tags.js'
import {DETAIL_MODE, LIST_MODE} from '../../common/constants/index.js'
import {
  PO_DATE_RANGE_FILTER_OPTIONS,
  PO_DATE_RANGE_FILTERS,
} from '../../common/constants/PurchaseOrderListDateRangeFilters.js'
import {toZonedTime} from '../../common/zonedTime.js'

export const PURCHASE_ORDER_LIST = 'PURCHASE_ORDER_LIST'

export function poListFormSelector(state) {
  return formsSelector(state)[PURCHASE_ORDER_LIST]
}

export const poIDListSelector = createSelector(
  poListFormSelector,
  ({poIDList} = {}) => poIDList || [],
)

export function getPODateRangeParams(
  startDate,
  endDate,
  dateFilterType,
  intervalType,
  intervalAmount,
  intervalTrunc,
  timezone,
) {
  const params = {timezone}

  if (startDate) {
    startDate = new Date(startDate)

    const startDateString = startDate.toISOString()

    if (PO_DATE_RANGE_FILTERS.includes(dateFilterType)) {
      params[`${dateFilterType}_after`] = startDateString
    }
  }

  if (endDate) {
    endDate = new Date(endDate)

    const endDateString = endDate.toISOString()

    if (PO_DATE_RANGE_FILTERS.includes(dateFilterType)) {
      params[`${dateFilterType}_before`] = endDateString
    }
  }

  if (intervalType) {
    intervalTrunc = ensureBoolean(intervalTrunc)

    if (PO_DATE_RANGE_FILTERS.includes(dateFilterType)) {
      params[`${dateFilterType}_interval_type`] = intervalType
      params[`${dateFilterType}_interval_amount`] = intervalAmount
      params[`${dateFilterType}_interval_trunc`] = intervalTrunc
    }
  }

  return params
}

export function summarizePODateRangeParams(query) {
  let startDate
  let endDate
  let dateFilterType
  let intervalType
  let intervalAmount
  let intervalTrunc
  let timezone

  if (query.timezone) {
    timezone = query.timezone
  }

  for (const dateType of PO_DATE_RANGE_FILTERS) {
    if (query[`${dateType}_after`]) {
      startDate = new Date(query[`${dateType}_after`])
      dateFilterType = dateType
    }
  }

  if (startDate && timezone) {
    startDate = toZonedTime(startDate, timezone)
  }

  for (const dateType of PO_DATE_RANGE_FILTERS) {
    if (query[`${dateType}_before`]) {
      endDate = new Date(query[`${dateType}_before`])
      dateFilterType = dateType
    }
  }

  if (endDate && timezone) {
    endDate = toZonedTime(endDate, timezone)
  }

  for (const dateType of PO_DATE_RANGE_FILTERS) {
    if (query[`${dateType}_interval_type`]) {
      intervalType = query[`${dateType}_interval_type`]
      intervalAmount = query[`${dateType}_interval_amount`]
      intervalTrunc = query[`${dateType}_interval_trunc`]
      dateFilterType = dateType
    }
  }

  return {
    startDate,
    endDate,
    dateFilterType,
    intervalType,
    intervalAmount,
    intervalTrunc,
    timezone,
  }
}

export function poListQuerySelector(state) {
  const cache = poListQuerySelector.cache

  const location = locationSelector(state)

  if (location === cache.location) {
    return cache.result
  }

  cache.location = location

  const {
    query,
    pathComponents: [resource, singleStatusOrPOID],
  } = location

  const po_id =
    !!singleStatusOrPOID && resource === PURCHASE_ORDER_SINGLE_URI_COMPONENT
      ? singleStatusOrPOID
      : null

  const result = {
    mode:
      resource === PURCHASE_ORDER_PLURAL_URI_COMPONENT ||
      (resource === PURCHASE_ORDER_SINGLE_URI_COMPONENT && !po_id)
        ? LIST_MODE
        : resource === PURCHASE_ORDER_SINGLE_URI_COMPONENT
          ? DETAIL_MODE
          : null,
    ...poListQueryToPOQuery(query, resource, singleStatusOrPOID),
  }

  if (isEqual(cache.result, result)) {
    return cache.result
  }

  cache.result = result

  return result
}
poListQuerySelector.cache = {}

export function poListQueryToPOQuery(query, resource, singleStatusOrPOID) {
  const status = [
    ...(resource === PURCHASE_ORDER_PLURAL_URI_COMPONENT
      ? singleStatusOrPOID
        ? PURCHASE_ORDER_STATUS_LIST.includes(singleStatusOrPOID)
          ? [singleStatusOrPOID]
          : []
        : [DEFAULT_STATUS]
      : ensureArray(query.status).filter((s) =>
          PURCHASE_ORDER_STATUS_LIST.includes(s),
        )),
  ]

  return {
    search: query.searchText || '',
    status,
    tag: ensureArray(query.tags),
    untagged: ensureTroolean(query.untagged) || false,
    tag_filter_by: query.tag_filter_by || TAG_FILTER_OR,
    exclude_tags: ensureArray(query.exclude_tags),
    supplier_id: ensureArrayOfNumbers(query.supplier_id),
    warehouse_id: ensureArrayOfNumbers(query.warehouse_id),
    sku: ensureArray(query.sku),
    po_id: ensureArray(query.po_id),
    ...getPODateRangeParams(
      query.startDate,
      query.endDate,
      query.dateFilterType,
      query.intervalType,
      query.intervalAmount,
      query.intervalTrunc,
      query.timezone,
    ),
    sort: query.sort || DEFAULT_SORT,
    limit: Number(query.limit) || DEFAULT_PER_PAGE,
    offset: Number(query.offset) || 0,
  }
}

export const isLoadingSelector = createSelector(
  poListFormSelector,
  ({isLoading} = {}) => isLoading || false,
)

export const countSelector = createSelector(
  poListFormSelector,
  ({count} = {}) => count || 0,
)

export const expandModeSelector = createSelector(
  poListFormSelector,
  ({expandMode} = {}) => expandMode || DEFAULT_EXPAND_MODE,
)

export const expandedPOIDsSelector = createSelector(
  poListFormSelector,
  ({expandedPOIDs} = {}) => expandedPOIDs || [],
)

export const selectedPOIDsSelector = createSelector(
  poListFormSelector,
  ({selectedPOIDs} = {}) => selectedPOIDs || [],
)

export const allSelectedSelector = createSelector(
  poIDListSelector,
  selectedPOIDsSelector,
  (poIDList, selected) =>
    poIDList.length > 0 && poIDList.length === selected.length,
)

export const firstSelectedPOIDSelector = createSelector(
  selectedPOIDsSelector,
  (selectedPOIDs) => selectedPOIDs[0],
)

export function countsSelector() {
  return PURCHASE_ORDER_STATII.reduce((prev, {slug}) => {
    prev[slug] = 0

    return prev
  }, {})
}

function hashBuilder(params) {
  let hash = `#/${PURCHASE_ORDER_SINGLE_URI_COMPONENT}`

  return stringifyURL(hash, params)
}

export const defaultHashParamsSelector = createSelector(
  poListFormSelector,
  ({sticky__limit, sticky__sort} = {}) => ({
    status: [DEFAULT_STATUS],
    limit: sticky__limit !== DEFAULT_PER_PAGE ? sticky__limit : undefined,
    sort: sticky__sort !== DEFAULT_SORT ? sticky__sort : undefined,
  }),
)

export const defaultPurchaseOrderListHashSelector = createSelector(
  defaultHashParamsSelector,
  hashBuilder,
)

export function cleanPOQuery(query) {
  return {
    status: ensureArrayOrUndefined(query.status),
    search: query.search?.trim() || undefined,
    tag: ensureArrayOrUndefined(query.tag),
    untagged: query.untagged || undefined,
    tag_filter_by:
      query.tag_filter_by !== TAG_FILTER_OR ? query.tag_filter_by : undefined,
    exclude_tags: ensureArrayOrUndefined(query.exclude_tags),
    supplier_id: ensureArrayOrUndefined(query.supplier_id),
    warehouse_id: ensureArrayOrUndefined(query.warehouse_id),
    sku: ensureArrayOrUndefined(query.sku),
    po_id: ensureArrayOrUndefined(query.po_id),
    timezone: query.timezone || undefined,
    ...PO_DATE_RANGE_FILTERS.reduce(
      (prev, dateType) => ({
        ...prev,
        [`${dateType}_after`]: query[`${dateType}_after`] || undefined,
        [`${dateType}_before`]: query[`${dateType}_before`] || undefined,
        [`${dateType}_interval_type`]:
          query[`${dateType}_interval_type`] || undefined,
        [`${dateType}_interval_amount`]:
          query[`${dateType}_interval_amount`] || undefined,
        [`${dateType}_interval_trunc`]:
          query[`${dateType}_interval_trunc`] || undefined,
      }),
      {},
    ),
    sort: query.sort,
    limit: query.limit,
    offset: query.offset,
  }
}

export function queryParamsSelector(state) {
  const query = poListQuerySelector(state)

  return cleanPOQuery(query)
}

export const createIsSelectedSelector = (poID) =>
  createSelector(selectedPOIDsSelector, (selectedPOIDs) => {
    return selectedPOIDs.includes(poID)
  })

export const createIsExpandedSelector = (poID) =>
  createSelector(
    expandedPOIDsSelector,
    expandModeSelector,
    (expandedPOIDs, expandMode) => {
      if (expandMode === EXPAND_MODE_COLLAPSED) {
        return expandedPOIDs.includes(poID)
      }
      if (expandMode === EXPAND_MODE_EXPANDED) {
        return !expandedPOIDs.includes(poID)
      }
      return false
    },
  )

export function getPODetailURL(poID) {
  return [[PURCHASE_ORDER_SINGLE_URI_COMPONENT, poID]]
}

export const createDetailURLSelector = (poID) => () =>
  '#' + buildPath(...getPODetailURL(poID))

export const isFilteredSelector = createSelector(
  poListQuerySelector,
  ({search, tag, exclude_tags, untagged, supplier_id, warehouse_id, sku}) =>
    isPresent(search) ||
    tag.length !== 0 ||
    untagged === true ||
    exclude_tags.length !== 0 ||
    supplier_id.length !== 0 ||
    warehouse_id.length !== 0 ||
    sku.length !== 0,
)

export function createPOTagQuantityMapSelector() {
  return createSelector(
    (state, {poIDs}) => poIDs,
    posSelector,
    poTagsSortedByNameSelector,
    (poIDs, pos, poTags) => {
      const poTagQuantityMap = poTags.reduce(
        (prev, tag) => ({
          ...prev,
          [tag.id]: 0,
        }),
        {},
      )

      poIDs.forEach((poID) => {
        const po = pos[poID]

        if (!po) {
          return
        }

        po.tags.forEach((tag) => {
          poTagQuantityMap[tag.id] += 1
        })
      })

      return mapValues(poTagQuantityMap, (selectedQuantity) => {
        const diff = poIDs.length - selectedQuantity
        if (diff === 0) {
          return 'all'
        } else if (diff === poIDs.length) {
          return 'none'
        }

        return 'some'
      })
    },
  )
}

export function getValidPODateRangeFilterTypes() {
  return PO_DATE_RANGE_FILTERS
}

export function validPODateRangeFilterTypesSelector() {
  return getValidPODateRangeFilterTypes()
}

export const validPODateRangeFilterTypesOptionsSelector = createSelector(
  validPODateRangeFilterTypesSelector,
  (validFilterTypes) =>
    validFilterTypes.map((filterType) =>
      PO_DATE_RANGE_FILTER_OPTIONS.find(({value}) => value === filterType),
    ),
)
